import { useState, useRef, useEffect } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  PaperPlaneRight, Sparkle, User, ArrowLeft, Trash, Microphone, SpeakerHigh, Stop
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check Web Speech API support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const speechSynthesis = window.speechSynthesis;

export default function ChatbotPage() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [analyzingIndex, setAnalyzingIndex] = useState(null);
  const [lastInputWasVoice, setLastInputWasVoice] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Load history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('gresart-chatbot-history');
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        console.error("Error loading chat history:", e);
      }
    }
  }, []);

  // Save history to localStorage
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('gresart-chatbot-history', JSON.stringify(history.slice(-50)));
    }
  }, [history]);

  // Auto-speak response when last input was voice
  useEffect(() => {
    if (lastInputWasVoice && history.length > 0 && !loading) {
      const lastMessage = history[history.length - 1];
      if (lastMessage.role === 'assistant') {
        speakText(lastMessage.content || lastMessage.summary || '', history.length - 1);
        setLastInputWasVoice(false);
      }
    }
  }, [history, loading, lastInputWasVoice]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  // Focus input on load
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'pt-PT';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setMessage(prev => prev + transcript);
        setIsListening(false);
        setLastInputWasVoice(true);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microfone não permitido');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      speechSynthesis?.cancel();
    };
  }, []);

  // Toggle voice input
  const toggleListening = () => {
    if (!SpeechRecognition) {
      toast.error('O seu navegador não suporta reconhecimento de voz');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error('Erro ao iniciar microfone');
      }
    }
  };

  // Speak text using TTS
  const speakText = (text, index) => {
    if (!speechSynthesis) {
      toast.error('O seu navegador não suporta síntese de voz');
      return;
    }

    // Stop if already speaking this message
    if (isSpeaking && speakingIndex === index) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      setSpeakingIndex(null);
      return;
    }

    // Cancel any ongoing speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pt-PT';
    utterance.rate = 0.9;
    utterance.pitch = 1;

    // Try to find Portuguese voice
    const voices = speechSynthesis.getVoices();
    const ptVoice = voices.find(v => v.lang.startsWith('pt'));
    if (ptVoice) {
      utterance.voice = ptVoice;
    }

    utterance.onstart = () => {
      setIsSpeaking(true);
      setSpeakingIndex(index);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setSpeakingIndex(null);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setSpeakingIndex(null);
    };

    speechSynthesis.speak(utterance);
  };

  // Set correct manifest for PWA "Add to Home Screen"
  useEffect(() => {
    // Change manifest link to assistant-specific manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.setAttribute('href', '/manifest-assistant.json');
    }
    
    // Update apple-mobile-web-app-title
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
      document.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute('content', 'Assistente IA');
    
    // Cleanup: restore original manifest when leaving
    return () => {
      if (manifestLink) {
        manifestLink.setAttribute('href', '/manifest.json');
      }
    };
  }, []);

  const sendMessage = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage("");
    const newHistory = [...history, { role: "user", content: userMessage }];
    setHistory(newHistory);
    setLoading(true);

    try {
      // Direct AI chat with real-time read access to the app's database (clients, sales,
      // Rappel, visits) and PDF catalogs/price tables - see /chatbot/ask on the backend.
      const response = await axios.post(`${API}/chatbot/ask`, {
        message: userMessage,
        history: newHistory.slice(-10)
      });
      setHistory(prev => [...prev, { role: "assistant", content: response.data.answer }]);
    } catch (error) {
      console.error("Chatbot error:", error);
      toast.error("Erro ao contactar o assistente");
      setHistory(prev => [...prev, { 
        role: "assistant", 
        content: "Desculpe, ocorreu um erro. Por favor tente novamente." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const analyzeWithAI = async (index, query) => {
    setAnalyzingIndex(index);
    try {
      const response = await axios.post(`${API}/chatbot/ask`, {
        message: query,
        history: history.slice(-10)
      });
      setHistory(prev => [...prev, { role: "assistant", content: response.data.answer }]);
    } catch (error) {
      console.error("Chatbot AI error:", error);
      toast.error("Erro ao analisar com IA");
      setHistory(prev => [...prev, { 
        role: "assistant", 
        content: "Desculpe, ocorreu um erro ao analisar com IA. Por favor tente novamente." 
      }]);
    } finally {
      setAnalyzingIndex(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('gresart-chatbot-history');
    toast.success("Histórico limpo");
  };

  return (
    <div className="h-screen max-h-screen bg-gradient-to-br from-stone-50 to-stone-100 dark:from-stone-900 dark:to-stone-950 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-gradient-to-r from-[#009392] to-[#00b3b0] text-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/')}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Voltar à app"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <img src="/assistant-icon.png" alt="Assistente IA" className="w-7 h-7 rounded-full bg-white" data-testid="chatbot-page-avatar-header" />
            <div>
              <h1 className="font-bold text-lg leading-tight">Assistente IA Gresart</h1>
              <p className="text-xs text-white/80">Pergunte sobre produtos, preços, clientes...</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={clearHistory}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Limpar histórico"
          >
            <Trash size={20} />
          </button>
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {history.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <img src="/mascot-full-body.png?v=13" alt="Assistente IA" className="w-[192px] sm:w-[235px] h-auto mb-3" data-testid="chatbot-page-avatar-empty" />
            <h2 className="text-lg font-semibold text-stone-700 dark:text-stone-300 mb-1">
              Olá! Sou o assistente Gresart
            </h2>
            <p className="text-sm text-stone-500 dark:text-stone-400 max-w-xs">
              Pergunte sobre clientes, vendas, Rappel, visitas ou produtos do catálogo.
            </p>
          </div>
        ) : (
          <>
            {history.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#009392] to-[#00b3b0] flex items-center justify-center">
                    <Sparkle size={16} className="text-white" weight="fill" />
                  </div>
                )}
                <div className="flex flex-col gap-1 max-w-[80%]">
                  <div
                    className={`rounded-2xl px-4 py-3 ${
                      msg.role === "user"
                        ? "bg-[#009392] text-white rounded-br-md"
                        : "bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-200 rounded-bl-md shadow-sm border border-stone-100 dark:border-stone-700"
                    }`}
                  >
                    {msg.kind === 'local_search' ? (
                      <div className="space-y-2" data-testid={`chatbot-page-local-result-${idx}`}>
                        <p className="text-sm font-medium">{msg.summary}</p>
                        {msg.results && msg.results.length > 0 && (
                          <div className="space-y-2 max-h-72 overflow-y-auto">
                            {msg.results.map((doc, di) => (
                              <div key={di} className="bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg p-2">
                                <p className="font-semibold text-[#009392] text-xs mb-1 truncate">{doc.pdf_name}</p>
                                {doc.matches.map((m, mi) => (
                                  <p key={mi} className="text-xs text-stone-600 dark:text-stone-400 leading-snug break-words">
                                    {m.page && <span className="text-stone-400">[Pág. {m.page}] </span>}
                                    {m.line}
                                  </p>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                        {msg.total_matches === 0 && msg.approximate_match && (
                          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2" data-testid={`chatbot-page-approximate-match-${idx}`}>
                            <p className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-1">Candidato mais parecido (aproximado):</p>
                            <p className="font-semibold text-[#009392] text-xs mb-0.5 truncate">{msg.approximate_match.pdf_name}</p>
                            <p className="text-xs text-stone-600 dark:text-stone-400 leading-snug break-words">
                              {msg.approximate_match.page && <span className="text-stone-400">[Pág. {msg.approximate_match.page}] </span>}
                              {msg.approximate_match.line}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {msg.role === "assistant" && msg.kind === 'local_search' && (
                      <button
                        onClick={() => analyzeWithAI(idx, msg.query)}
                        disabled={analyzingIndex === idx}
                        className="self-start flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#009392]/10 text-[#009392] hover:bg-[#009392]/20 transition-colors disabled:opacity-60"
                        data-testid={`chatbot-page-analyze-ai-btn-${idx}`}
                      >
                        {analyzingIndex === idx ? (
                          <>
                            <LoadingSpinner size={14} />
                            <span>A analisar...</span>
                          </>
                        ) : (
                          <>
                            <Sparkle size={12} />
                            <span>Analisar com IA?</span>
                          </>
                        )}
                      </button>
                    )}
                    {msg.role === "assistant" && msg.kind !== 'local_search' && speechSynthesis && (
                      <button
                        onClick={() => speakText(msg.content, idx)}
                        className={`self-start flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                          isSpeaking && speakingIndex === idx
                            ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-stone-100 text-stone-500 hover:bg-stone-200 dark:bg-stone-700 dark:text-stone-400 dark:hover:bg-stone-600'
                        }`}
                        data-testid={`speak-message-${idx}`}
                      >
                        {isSpeaking && speakingIndex === idx ? (
                          <>
                            <Stop size={12} weight="fill" />
                            <span>Parar</span>
                          </>
                        ) : (
                          <>
                            <SpeakerHigh size={12} />
                            <span>Ouvir</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {msg.role === "user" && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-stone-200 dark:bg-stone-700 flex items-center justify-center">
                    <User size={16} className="text-stone-600 dark:text-stone-300" weight="fill" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#009392] to-[#00b3b0] flex items-center justify-center">
                  <Sparkle size={16} className="text-white animate-pulse" weight="fill" />
                </div>
                <div className="bg-white dark:bg-stone-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-stone-100 dark:border-stone-700">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-[#009392] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-[#009392] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-[#009392] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800 safe-area-bottom">
        <div className="max-w-3xl mx-auto flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? "A ouvir..." : "Escreva a sua pergunta..."}
            className={`flex-1 px-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#009392]/50 dark:bg-stone-800 dark:text-white ${
              isListening 
                ? 'border-red-400 bg-red-50 dark:bg-red-900/20' 
                : 'border-stone-200 dark:border-stone-700'
            }`}
            disabled={loading}
          />
          {SpeechRecognition && (
            <Button
              onClick={toggleListening}
              disabled={loading}
              variant={isListening ? "destructive" : "outline"}
              className={`px-4 rounded-xl ${isListening ? 'animate-pulse' : ''}`}
              data-testid="chatbot-page-voice-btn"
            >
              <Microphone size={20} weight={isListening ? "fill" : "regular"} />
            </Button>
          )}
          <Button
            onClick={sendMessage}
            disabled={!message.trim() || loading}
            className="btn-primary px-4 rounded-xl"
          >
            <PaperPlaneRight size={20} weight="fill" />
          </Button>
        </div>
      </div>
    </div>
  );
}
